import dynamic from "next/dynamic";
import { useState } from "react";
import { ErrorMessage, useFormikContext } from "formik";
import { SpeckleImportModel } from "../../types/SpeckleImportModel";
import FormError from "components/elements/Forms/FormError";
import SpeckleTitle from "../../SpeckleTitle";
import SpeckleBreadcrumbs, {
  SpeckleCrumb,
  SpeckleCrumbSeparator,
} from "../../SpeckleBreadcrumbs";
import Loading from "components/elements/Loading/Loading";
import ParticleLoading from "components/animations/particle-loading.json";
import WarningTriangleIcon from "components/icons/24/notifications/warning-triangle.svg";
import { SpeckleServerCard } from "../../controls/SpeckleServerCard";
import { nameof } from "lib/actions/util";
import { useSpeckleApps } from "lib/hooks/useSpeckleApps";
import { usePopupAuthCallback } from "../../../lib/hooks/usePopupAuthCallback";
import { useWizard } from "components/elements/Wizard/useWizard";
import { SpeckleAddAccountAuthStatus as AuthStatus } from "../../types/SpeckleAddAccountAuthStatus";
import { useCurrentProject } from "lib/hooks/useProject";
import { datadogLogs } from "@datadog/browser-logs";

const LottiePlayer = dynamic(
  () => import("components/animations/LottiePlayer"),
  {
    ssr: false,
  },
);

export default function SpeckleAccountAddStepVariant() {
  const { stepViewVariant } = useWizard();
  const returnToAccounts = () => stepViewVariant(null);
  const { values } = useFormikContext<SpeckleImportModel>();
  return (
    <div>
      <div className="grid grid-cols-2 gap-8">
        <header>
          <SpeckleTitle />
        </header>
        <div className="space-y-5">
          <SpeckleBreadcrumbs>
            <SpeckleCrumb onClick={returnToAccounts}>Accounts</SpeckleCrumb>
            <SpeckleCrumbSeparator />
            <SpeckleCrumb active>Add Account</SpeckleCrumb>
          </SpeckleBreadcrumbs>
        </div>
      </div>
      <div className="h-full select-none space-y-8 overflow-y-auto pb-8 pr-4">
        <AddAccountDialog
          onSubmit={returnToAccounts}
          onExit={returnToAccounts}
        />
      </div>
      <ErrorMessage name={nameof(values, "projectId")} component={FormError} />
    </div>
  );
}

function AddAccountDialog(p: { onSubmit: () => void; onExit: () => void }) {
  const { project } = useCurrentProject();
  const { apps, isLoading, hasError } = useSpeckleApps(project?.workspace.id);
  const [currentAuthStep, setCurrentAuthStep] = useState<AuthStatus>(
    AuthStatus.NOT_STARTED,
  );

  const onAuthStatusUpdate = (status: AuthStatus) => {
    if (status === AuthStatus.COMPLETE) {
      p.onSubmit();
      return;
    }
    setCurrentAuthStep(status);
  };
  const onAuthError = (message: string, error?: Error) => {
    console.error("Speckle add account auth error: ", message, error);
    datadogLogs.logger.error(
      "Speckle add account auth error: ",
      undefined,
      new Error(message, { cause: error }),
    );
  };
  const { beginAuth } = usePopupAuthCallback(
    currentAuthStep,
    onAuthStatusUpdate,
    onAuthError,
  );

  return (
    <div className="mb-10 px-20 pt-20">
      {currentAuthStep === AuthStatus.NOT_STARTED && (
        <div>
          <header className="mb-4">
            <h1>Add Account</h1>
            <p className="my-2 text-sm text-neutral-700">
              Select a Speckle server to connect to
            </p>
          </header>
          {isLoading && <Loading className="mt-20" />}
          {hasError && (
            <div className="flex flex-col items-center space-y-2 p-6 pb-8">
              <WarningTriangleIcon className="w-10 text-red-500" />
              <div className="text-center text-sm font-light">
                There was an error getting the list of available Speckle
                servers.
              </div>
            </div>
          )}
          {!isLoading && !hasError && (
            <div className="space-y-3">
              {apps.map((x) => (
                <SpeckleServerCard
                  key={x.id}
                  app={x}
                  beginAuth={() => beginAuth(x.id)}
                />
              ))}
              {apps.length === 0 && (
                <p className="mt-10 text-center text-sm text-gray-400">
                  No servers available
                </p>
              )}
            </div>
          )}
        </div>
      )}
      {currentAuthStep === AuthStatus.AUTHENTICATING && (
        <div className="flex w-full flex-col items-center">
          <LottiePlayer
            lottieAnimation={ParticleLoading}
            className="mb-8 mt-10 w-40"
          />
          <p className="mt-4 text-xl font-light">
            Connecting to Speckle in a new window.
          </p>
          <button onClick={p.onExit} className="my-6 text-neutral-400">
            Cancel
          </button>
        </div>
      )}
      {currentAuthStep === AuthStatus.ERROR && (
        <div className="flex w-full flex-col items-center">
          <WarningTriangleIcon className="mb-8 mt-10 w-16 text-red-500" />
          <div className="w-2/3 space-y-1 text-center">
            <p className="text-2xl font-light">Something went wrong.</p>
            <p className="pb-10 text-lg font-thin">
              An unexpected interruption occurred during the authentication
              process.
            </p>
            <button
              type="button"
              className="mx-auto flex items-center justify-center rounded-lg bg-black p-3 px-6 font-thin text-white hover:bg-gray-800 focus:ring-offset-2"
              onClick={p.onExit}
            >
              Return to the accounts page
            </button>
          </div>
        </div>
      )}
      {currentAuthStep === AuthStatus.POPUP_BLOCKED && (
        <div className="flex w-full flex-col items-center">
          <WarningTriangleIcon className="mb-8 mt-10 w-16 text-yellow-500" />
          <div className="w-2/3 space-y-1 text-center">
            <p className="text-2xl font-light">Popup blocked</p>
            <p className="pb-10 text-lg font-thin">
              Your browser blocked the authentication window. Allow popups for
              this site and try again.
            </p>
            <button
              type="button"
              className="mx-auto flex items-center justify-center rounded-lg bg-black p-3 px-6 font-thin text-white hover:bg-gray-800 focus:ring-offset-2"
              onClick={() => setCurrentAuthStep(AuthStatus.NOT_STARTED)}
            >
              Try again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
